import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, catchError, map, of, startWith, take, tap } from 'rxjs';
import { BrandingApiServiceObservable, GetReceiptsLookupBrandingResponse } from '@kempoweroyj/ngx-branding-api-client';
import { ConfigService } from '@kempower-nx-workspace/receipt-lookup-environment';

export interface GetBrandingResult {
    loading: boolean;
    data?: GetReceiptsLookupBrandingResponse;
    error?: string;
}

@Injectable({
    providedIn: 'root',
})
export class BrandingService {
    readonly #brandingApiService: BrandingApiServiceObservable = inject(BrandingApiServiceObservable);
    readonly #configService: ConfigService = inject(ConfigService);

    readonly #brandingStateSubject: BehaviorSubject<GetBrandingResult> = new BehaviorSubject<GetBrandingResult>({
        loading: false,
    });

    // Expose the branding state as an observable
    readonly brandingState$: Observable<GetBrandingResult> = this.#brandingStateSubject.asObservable();

    getBranding$(): Observable<GetBrandingResult> {
        const customerId = this.#configService.extractedCustomerId;
        if (!customerId) {
            return of({ loading: false, data: undefined, error: 'Customer ID is not defined.' });
        }
        return this.#brandingApiService.getReceiptsLookupBranding(customerId).pipe(
            map((data: GetReceiptsLookupBrandingResponse): GetBrandingResult => {
                const result: GetBrandingResult = { loading: false, data: data, error: undefined };
                return result;
            }),
            startWith({
                loading: true,
                data: undefined,
                error: undefined,
            } as GetBrandingResult),
            catchError((error: unknown): Observable<GetBrandingResult> => {
                return of({ loading: false, data: undefined, error: (error as Error)?.message });
            }),
            tap((result: GetBrandingResult) => {
                this.#brandingStateSubject.next(result);
            })
        );
    }

    ensureBrandingLoaded$(): Observable<GetBrandingResult> {
        const result: GetBrandingResult = this.getCurrentBrandingState();
        if (!result.loading && !result.data && !result.error) {
            // Avoid race where multiple callers trigger getBranding$ at the same time
            this.#brandingStateSubject.next({ loading: true });
            return this.getBranding$();
        }
        // Already have a result (success, error or currently loading) – emit one value
        return this.brandingState$.pipe(take(1));
    }

    getCurrentBrandingState(): GetBrandingResult {
        return this.#brandingStateSubject.value;
    }

    // Helper method to get just the logo URL from the branding data
    getLogoUrl(): string | undefined {
        const currentState: GetBrandingResult = this.getCurrentBrandingState();
        const logoUrl: string | undefined = currentState.data?.branding?.logoUrl;

        if (!logoUrl) {
            return undefined;
        }

        // URL form validation
        try {
            new URL(logoUrl);
            // TODO: Add additional validation
            return logoUrl; // Return logoUrl if valid
        } catch {
            return undefined; // Return undefined on invalid
        }
    }

    // Helper method to get just the supportContact from the branding data
    getSupportContact(): string | undefined {
        const currentState: GetBrandingResult = this.getCurrentBrandingState();
        return currentState.data?.branding?.receiptLookup?.supportContact;
    }
}
